// Pre-join -> token -> authoritative notice -> room. The notice is pre-join
// only; inside the room the "Gravar" control shows and changes the status.
import type { RecordingStatus, TokenResponse } from '@kima-hub/contracts';
import { useEffect, useState } from 'react';
import { isApiError, userMessage } from '../api/client.js';
import { COPY } from '../copy.js';
import { navigate, paths } from '../router.js';
import { useServices } from '../services.js';
import { useNarrow } from '../ui/useNarrow.js';
import { PreJoin } from './PreJoin.js';
import type { JoinChoices } from './PreJoin.js';
import { RecordButton } from './RecordButton.js';
import { useRecordingControl } from './useRecordingControl.js';

type Stage =
  | { kind: 'prejoin'; error: string | null }
  | { kind: 'requesting'; choices: JoinChoices }
  | { kind: 'notice'; choices: JoinChoices; token: TokenResponse }
  | { kind: 'room'; choices: JoinChoices; token: TokenResponse }
  | { kind: 'not_found' };

/** Re-issue rather than connect with a token this close to expiry. */
const TOKEN_EXPIRY_MARGIN_MS = 5000;

export function RecordingNotice({ recording }: { recording: RecordingStatus }) {
  const text =
    recording.mode === 'recorded'
      ? COPY.recordedNotice
      : recording.unavailable_reason === 'not_started'
        ? COPY.notStartedNotice
        : COPY.unavailableNotice;
  return (
    <p className="recording-notice muted small" role="status" data-testid="recording-notice" data-mode={recording.mode}>
      {text}
    </p>
  );
}

function InRoom({ slug, choices, token }: { slug: string; choices: JoinChoices; token: TokenResponse }) {
  const { api, LiveRoom } = useServices();
  const control = useRecordingControl({ api, meetingId: token.meeting_id, slug, initial: token.recording });
  return (
    <LiveRoom
      serverUrl={token.server_url}
      token={token.token}
      audio={choices.audio}
      video={choices.video}
      audioDeviceId={choices.audioDeviceId}
      videoDeviceId={choices.videoDeviceId}
      onLeave={() => navigate(paths.home())}
      controls={
        <RecordButton
          status={control.status}
          pending={control.pending}
          error={control.error}
          onPress={() => void control.start().catch(() => undefined)}
        />
      }
      onRecordingSignal={control.onSignal}
      bindRecordingSignal={control.bindSender}
    />
  );
}

export function RoomPage({ slug }: { slug: string }) {
  const { api, media } = useServices();
  const narrow = useNarrow();
  const [stage, setStage] = useState<Stage>({ kind: 'prejoin', error: null });
  const [liveHint, setLiveHint] = useState(false);

  useEffect(() => {
    const ctl = new AbortController();
    api.presence(ctl.signal).then(
      (p) => setLiveHint(p.rooms.some((r) => r.slug === slug && r.recording !== null)),
      () => undefined,
    );
    return () => ctl.abort();
  }, [api, slug]);

  const requestToken = (choices: JoinChoices) => {
    setStage({ kind: 'requesting', choices });
    api.token({ room_slug: slug, shared_mic: choices.sharedMic }).then(
      (token) => setStage({ kind: 'notice', choices, token }),
      (err: unknown) => {
        if (isApiError(err, 'room_not_found')) setStage({ kind: 'not_found' });
        else setStage({ kind: 'prejoin', error: userMessage(err) });
      },
    );
  };

  const enter = (choices: JoinChoices, token: TokenResponse) => {
    if (Date.parse(token.expires_at) - Date.now() < TOKEN_EXPIRY_MARGIN_MS) {
      // The notice must match the token actually used: ask again and show the new status.
      requestToken(choices);
      return;
    }
    setStage({ kind: 'room', choices, token });
  };

  switch (stage.kind) {
    case 'not_found':
      return (
        <div className="page">
          <p className="error" role="alert">
            {COPY.roomNotFound}
          </p>
          <button type="button" onClick={() => navigate(paths.home())}>
            {COPY.backHome}
          </button>
        </div>
      );
    case 'prejoin':
    case 'requesting':
      return (
        <PreJoin
          slug={slug}
          media={media}
          liveHint={liveHint}
          busy={stage.kind === 'requesting'}
          error={stage.kind === 'prejoin' ? stage.error : null}
          narrow={narrow}
          onJoin={requestToken}
        />
      );
    case 'notice':
      return (
        <div className="page notice-step" data-testid="notice-step">
          <h1>{COPY.roomTitle(stage.token.room.title)}</h1>
          <div className="actions">
            <button type="button" className="button primary" onClick={() => enter(stage.choices, stage.token)}>
              {COPY.confirmEnter}
            </button>
            <button type="button" onClick={() => setStage({ kind: 'prejoin', error: null })}>
              {COPY.cancel}
            </button>
          </div>
          <RecordingNotice recording={stage.token.recording} />
        </div>
      );
    case 'room':
      return (
        <div className={narrow ? 'room room--narrow' : 'room'} data-testid="in-room">
          <InRoom slug={slug} choices={stage.choices} token={stage.token} />
        </div>
      );
  }
}
